import { FieldArray, FormikProps, useFormikContext } from 'formik'
import {
  BoxV4,
  ButtonV2,
  LabraToolTipV2,
  SelectV4,
  TextInputV4,
} from '@labrav/react-components'

import { get, isNil } from 'lodash'
import { DimensionType } from '../../../../modules/privateOffer/reducer'
import { SelectChangeEvent, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShelves, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { useCallback, useEffect, useMemo } from 'react'
import clsx from 'clsx'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { Product } from '../../../../modules/productsListing/reducer'
import { useStyles } from './PlansCombinedForm.styles'
import { faPlus } from '@fortawesome/free-solid-svg-icons'

import {
  CombinedFormDimension,
  CombinedFormState,
} from '../../../../modules/combinedForm/reducer'
import { HelpDrawer } from '@labrav/common'
import { useSelector } from '../../../../store'

interface PlansCombinedFormProps {
  heading?: React.ReactNode
  subHeading?: React.ReactNode
  addButtonName: string
  disabled: boolean
}

export const PlansCombinedForm = ({
  subHeading,
  heading,
  addButtonName,
  disabled,
}: PlansCombinedFormProps) => {
  const { classes } = useStyles()
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext() as FormikProps<CombinedFormState>
  const dimensions = (values.resellerAgreementData.dimensions ||
    []) as CombinedFormDimension[]
  const standardDimensionsData = dimensions?.filter(
    dimensionItem => dimensionItem.type === 'standard'
  )
  const dimensionsLength = standardDimensionsData.length
  const customDimensionLength = dimensions?.filter(
    dimensionItem => dimensionItem.type === 'custom'
  ).length
  const standardCustomDimensionLength = dimensionsLength + customDimensionLength
  const products = useSelector<Product[]>(
    state => state.productsListing['AWS']?.products?.rows || []
  )
  const emptyObjectDimensions = {
    label: '',
    labelDescription: '',
    type: 'standard' as DimensionType,
    currency: 'USD',
  }
  const productIdToUse = values.resellerAgreementData.awsProductId
  const product =
    products.find(product => product.productId === productIdToUse) ||
    ({} as Product)

  const productDimensions = useMemo(() => {
    return (
      product?.productDimensions?.filter(
        dimension => dimension.isAdditionalUsageAllowed === false
      ) || []
    )
  }, [product])
  const selectedDimensionNames = standardDimensionsData.map(
    dimension => dimension.name
  )
  const dimensionsOptions = useMemo(() => {
    return productDimensions.map(dimension => ({
      label: dimension.label,
      value: dimension.name,
    }))
  }, [productDimensions, dimensions])

  const handleChangeSelect = useCallback(
    (event: SelectChangeEvent, index: number) => {
      const selectedLabel = event.target.value as string
      const {
        id,
        price,
        freeTrial,
        valueDataType,
        // prices,
        ...selectedDimension
      } =
        productDimensions.find(dimension => dimension.name === selectedLabel) ||
        {}

      if (selectedDimensionNames) {
        const updatedDimension = {
          ...selectedDimension,
          type: 'standard',
          dimensionId: id,
          quantity: '',
          price: '',
        }
        setFieldValue(
          `resellerAgreementData.dimensions.${index}`,
          updatedDimension
        )
        setFieldTouched(
          `resellerAgreementData.dimensions.${index}.quantity`,
          false
        )
      }
    },
    [productDimensions]
  )

  useEffect(() => {
    const nonAdditionalUsageDimension = dimensions.filter(
      dimension => dimension.type === 'standard' || dimension.type === 'custom'
    )
    if (nonAdditionalUsageDimension.length === 0) {
      dimensions.push(emptyObjectDimensions)
    }
  }, [dimensions])

  return (
    <>
      <BoxV4
        heading={'Plans'}
        headingIcon={
          <FontAwesomeIcon
            icon={faShelves}
            style={{ width: '18px', height: '14px' }}
          />
        }
        secondaryActions={
          <HelpDrawer
            sectionName="combinedFormPlans"
            headerTitle="Help & FAQs"
          />
        }
      >
        {heading && (
          <Typography className={classes.heading}>{heading}</Typography>
        )}
        {subHeading && (
          <Typography className={classes.value}>{subHeading}</Typography>
        )}
        <div className={clsx(classes.root, classes.elementsGap)}>
          <div className={classes.titleWraper}>
            <Typography
              className={clsx(classes.sectionText, classes.columnName)}
            >
              Plan
            </Typography>
            <div
              className={clsx(classes.sectionText, classes.columnDesc)}
            ></div>
            <Typography
              className={clsx(
                classes.sectionText,
                classes.columnPrice,
                classes.textAlign
              )}
            >
              Quantity
            </Typography>
            <div style={{ width: '2%' }}></div>
          </div>
          <div className={classes.border}></div>
          <FieldArray name="resellerAgreementData.dimensions">
            {({ push, remove }) => (
              <>
                {dimensions.map((dimension, index) => {
                  const dimensionKey = dimension.id || index
                  return dimension.type === 'standard' ? (
                    <>
                      <div
                        className={classes.resourceRow}
                        data-testid="dimensions-row"
                      >
                        <div className={classes.resourcesContainer}>
                          <div
                            className={classes.columnName}
                            key={`resellerAgreementData.dimensions-${
                              dimension.id || index
                            }-paymentDate`}
                          >
                            <SelectV4
                              field={{
                                title: 'Select a plan',
                                placeHolder: 'Select a plan',
                                humanReadableKey: 'dimension',
                                id: `resellerAgreementData.dimensions.${[index]}.label`,
                                isRequired: false,
                                isReadOnly: disabled,
                                isCustom: false,
                                defaultValue: '',
                                dataType: 'enum' as DataType,
                                options: dimensionsOptions.filter(
                                  dimension =>
                                    !selectedDimensionNames
                                      .filter(
                                        id =>
                                          id !==
                                          values.resellerAgreementData
                                            .dimensions[index].name
                                      )
                                      .includes(dimension.value)
                                ),
                              }}
                              value={
                                values.resellerAgreementData.dimensions[index]
                                  .name || ''
                              }
                              onChange={e => handleChangeSelect(e, index)}
                              onBlur={handleBlur}
                              isError={
                                !!(
                                  get(
                                    touched,
                                    `resellerAgreementData.dimensions.${[index]}.label`,
                                    false
                                  ) &&
                                  get(
                                    errors,
                                    `resellerAgreementData.dimensions.${[index]}.label`,
                                    false
                                  )
                                )
                              }
                              errorText={
                                (!!get(
                                  touched,
                                  `resellerAgreementData.dimensions.${[index]}.label`,
                                  false
                                ) &&
                                  get(
                                    errors,
                                    `resellerAgreementData.dimensions.${[index]}.label`,
                                    ''
                                  )) ||
                                ''
                              }
                            />
                          </div>
                          <div
                            className={clsx(
                              classes.alignDescription,
                              classes.wordWrap,
                              classes.columnDesc
                            )}
                            key={`dimensions-${dimensionKey}-desc`}
                            data-testid="description-select"
                          ></div>

                          <div
                            key={`dimensions-${dimensionKey}-quantity`}
                            className={classes.columnPrice}
                          >
                            <TextInputV4
                              keyName={`resellerAgreementData.dimensions.${[index]}.quantity`}
                              title="Quantity"
                              type="number"
                              placeHolder=""
                              value={
                                !isNil(dimensions[index]?.quantity)
                                  ? (dimensions[index]?.quantity as number)
                                  : ''
                              }
                              updatedValue={
                                !isNil(dimensions[index]?.quantity)
                                  ? (dimensions[index]?.quantity as number)
                                  : ''
                              }
                              isError={
                                !!(
                                  get(
                                    touched,
                                    `resellerAgreementData.dimensions.${[index]}.quantity`,
                                    false
                                  ) &&
                                  get(
                                    errors,
                                    `resellerAgreementData.dimensions.${[index]}.quantity`,
                                    false
                                  )
                                )
                              }
                              errorText={
                                (!!get(
                                  touched,
                                  `resellerAgreementData.dimensions.${[index]}.quantity`,
                                  false
                                ) &&
                                  get(
                                    errors,
                                    `resellerAgreementData.dimensions.${[index]}.quantity`,
                                    ''
                                  )) ||
                                ''
                              }
                              onChange={handleChange}
                              isReadOnly={disabled}
                              isRequired={false}
                              showSpinner={false}
                              onBlur={handleBlur}
                              keyToPrevent={['+', '-', 'e']}
                            />
                          </div>
                        </div>
                        {standardCustomDimensionLength > 1 ? (
                          <div className={classes.trashIcon}>
                            <FontAwesomeIcon
                              icon={faTrash}
                              style={{ width: '18px', height: '20px' }}
                              data-testid="trash-dimensions"
                              onClick={() => remove(index)}
                            />
                          </div>
                        ) : (
                          <div style={{ width: '2%' }}></div>
                        )}
                      </div>
                      <div className={classes.border}></div>
                    </>
                  ) : (
                    <></>
                  )
                })}
                {dimensionsLength <= productDimensions.length - 1 ? (
                  <div>
                    <ButtonV2
                      className={classes.widthButton}
                      styleType="link"
                      onClick={() => {
                        push(emptyObjectDimensions)
                      }}
                      disabled={disabled}
                      data-testid="add-a-dimensions-button"
                      icon={
                        <FontAwesomeIcon
                          icon={faPlus}
                          style={{ width: '13px', height: '14px' }}
                        />
                      }
                      smallVariant
                    >
                      {addButtonName}
                    </ButtonV2>
                  </div>
                ) : (
                  <div data-testid="add-a-dimensions-button">
                    <LabraToolTipV2
                      tooltipPlacement="bottom"
                      description={
                        'All existing dimensions have already been added.'
                      }
                    >
                      <ButtonV2
                        styleType="link"
                        onClick={() => void 0}
                        data-testid="add-a-dimensions-button"
                        icon={
                          <FontAwesomeIcon
                            icon={faPlus}
                            style={{ width: '13px', height: '14px' }}
                          />
                        }
                        disabled={true}
                        smallVariant
                      >
                        {addButtonName}
                      </ButtonV2>
                    </LabraToolTipV2>
                  </div>
                )}
              </>
            )}
          </FieldArray>
        </div>
      </BoxV4>
    </>
  )
}
